import React from "react";
import { LoaderFunction, RouteObject } from "react-router";
import { UserContext } from "@/context/UserContext";
import { MockScope } from "@/libs/mockScope";
import { composeFeatureFlagLoader } from "@/Routes/featureFlagged";
import { ConfigParameter } from "@/types/api";

// React Router's lazy() returns at least { Component } here — our route
// helpers (createLazyRoute / authRoute) always populate it.
type LazyResult = { Component: React.ComponentType };
type LazyRouteEntry = Omit<RouteObject, "lazy"> & {
  lazy?: () => Promise<LazyResult>;
};

/**
 * The flag that gates a mock-scoped route, alone or with:
 *
 * - `wrapper`: rendered around the page, outside <MockScope> - for a provider
 *   that must read the real API (e.g. the tenant's saved enforcement) before
 *   the page mounts.
 * - `mockWhen`: whether to mount <MockScope> at all (default: always). Read at
 *   render time; when it returns false the page renders against the real API.
 * - `enabledWhen` (`mockScopedSwapRoute` only): an extra condition on top of
 *   `flag` for picking the enabled page over the fallback.
 */
export type MockScopedConfig =
  | ConfigParameter
  | {
      flag: ConfigParameter;
      wrapper?: React.ComponentType<React.PropsWithChildren>;
      mockWhen?: () => boolean;
      enabledWhen?: () => boolean;
    };

type ResolvedConfig = Exclude<MockScopedConfig, ConfigParameter>;

const resolveConfig = (config: MockScopedConfig): ResolvedConfig =>
  typeof config === "string" ? { flag: config } : config;

const wrapComponent = (
  Component: React.ComponentType,
  { wrapper: Wrapper, mockWhen }: ResolvedConfig,
): React.ComponentType => {
  const Wrapped: React.FC = () => {
    const content =
      (mockWhen?.() ?? true) ? (
        <MockScope>
          <Component />
        </MockScope>
      ) : (
        <Component />
      );
    return Wrapper ? <Wrapper>{content}</Wrapper> : content;
  };
  Wrapped.displayName = `MockScoped(${Component.displayName || Component.name || "Component"})`;
  return Wrapped;
};

/**
 * Declare one or more routes that are gated by a tenant feature flag AND
 * render under a <MockScope>. Inside such a route, useQuery/useMutation will
 * resolve mocked methods from the in-memory registry; unmocked methods fall
 * through to the real API.
 *
 *   ...mockScopedRoutes("isAgentPreviewEnabled",
 *     authRoute("/app/agents/preview", () => import("@/pages/AgentPreview")),
 *   )
 *
 * Pair with a navigation entry that has `featureConfig: flag` so the sidebar
 * also hides the link when the tenant lacks the flag.
 */
export const mockScopedRoutes = (
  config: MockScopedConfig,
  ...routes: LazyRouteEntry[]
): RouteObject[] => {
  const resolved = resolveConfig(config);
  return routes.map(route => {
    const originalLazy = route.lazy;
    return {
      ...route,
      loader: composeFeatureFlagLoader(
        resolved.flag,
        route.loader as LoaderFunction | undefined,
      ),
      lazy: originalLazy
        ? async () => {
            const mod = await originalLazy();
            return { Component: wrapComponent(mod.Component, resolved) };
          }
        : undefined,
    } as RouteObject;
  });
};

/**
 * One path, two pages. Resolves `whenEnabled` under <MockScope> if the tenant
 * has `flag`, otherwise `fallback` untouched:
 *
 *   mockScopedSwapRoute("isAgentPreviewEnabled",
 *     authRoute(URLs.dashboard, () => import("@/pages/NewDashboard")),
 *     authRoute(URLs.dashboard, () => import("@/pages/Dashboard")),
 *   )
 *
 * Unlike `mockScopedRoutes` this never redirects, so use it for a route that
 * must stay reachable with the flag off — notably the default landing route,
 * which a feature-flag redirect to `URLs.root` would bounce back and forth
 * forever (see `Routes/loaders`).
 *
 * Only `whenEnabled`'s `lazy` is used: the route's `path` and `loader` both
 * come from `fallback`, so don't put anything on the enabled branch expecting
 * it to run. The flag is read once per router, when the path is first
 * navigated to and React Router resolves (and memoizes) the module — fine
 * because config parameters only arrive at login, and logging out is a full
 * document load. That memoization is also why a mid-session
 * `?overrides=<flag>` does not swap the page until a full reload.
 */
export const mockScopedSwapRoute = (
  config: MockScopedConfig,
  whenEnabled: LazyRouteEntry,
  fallback: LazyRouteEntry,
): RouteObject => {
  const resolved = resolveConfig(config);
  const { flag, enabledWhen } = resolved;
  return {
    // Keeps the fallback's path and loader; deliberately no feature-flag loader.
    ...fallback,
    lazy: async () => {
      const enabled =
        UserContext.hasConfigParameter(flag) && (enabledWhen?.() ?? true);
      const route = enabled ? whenEnabled : fallback;
      if (!route.lazy) {
        throw new Error(
          `mockScopedSwapRoute: the ${enabled ? "enabled" : "fallback"} entry for "${flag}" has no lazy loader`,
        );
      }
      const mod = await route.lazy();
      return {
        Component: enabled
          ? wrapComponent(mod.Component, resolved)
          : mod.Component,
      };
    },
  } as RouteObject;
};
